import { useState, type ReactNode } from "react"

import {
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  FormField,
  MultiSelect,
  Textarea,
} from "@coval/ui"
import {
  CalendarClock,
  ChevronDown,
  FileText,
  SlidersHorizontal,
} from "lucide-react"

import { reportRangeError } from "../../utils/reportPeriods"
import { reportScopeCapacity } from "../../utils/reportScopeCapacity"
import type {
  Bootstrap,
  Cadence,
  ReportConfig,
  Scope,
} from "../../utils/reportTypes"
import { Choice } from "./ReportControls"
import { ReportPeriodPicker } from "./ReportPeriodPicker"
import { ReportRepeatPicker } from "./ReportRepeatPicker"
import { reportClass } from "./ReportStyles"
import { UploadedAgentSelection } from "./UploadedAgentSelection"

function toggle(values: string[], id: string): string[] {
  return values.includes(id)
    ? values.filter((value) => value !== id)
    : [...values, id]
}

export function Configuration({
  config,
  onChange,
  repeat = null,
  onRepeatChange,
  repeatCadences,
  bootstrap,
  disabled,
  onGenerate,
  onScheduleNotebook,
  savedRecipes,
}: {
  config: ReportConfig
  onChange: (config: ReportConfig) => void
  /** How often the generated notebook repeats; null for a one-time notebook. */
  repeat?: Cadence | null
  onRepeatChange?: (repeat: Cadence | null) => void
  repeatCadences?: Cadence[]
  bootstrap: Bootstrap
  disabled: boolean
  onGenerate: () => void
  /** With a notebook open and a cadence chosen: repeat it without generating another. */
  onScheduleNotebook?: () => void
  savedRecipes?: ReactNode
}) {
  const scope = config.scope
  const capacity = reportScopeCapacity(scope, bootstrap.scope_limits)
  const periodError = reportRangeError(config)
  const monitoringOnly = scope.source === "monitoring"
  // Only an intentionally empty manual selection needs local UI state.
  // Loaded recipes derive their mode directly from their saved scope.
  const [emptyManualScope, setEmptyManualScope] = useState<Scope | null>(null)
  const manual = scope.agent_ids.length > 0 || scope === emptyManualScope
  const invalid =
    Boolean(capacity.error) ||
    Boolean(periodError) ||
    (!monitoringOnly && manual && !scope.agent_ids.length)
  const updateScope = (change: Partial<Scope>, chooseManually = manual) => {
    const nextScope = { ...scope, ...change }
    setEmptyManualScope(
      chooseManually && !nextScope.agent_ids.length ? nextScope : null,
    )
    onChange({ ...config, scope: nextScope })
  }
  return (
    <>
      <section
        className={reportClass("configuration")}
        aria-label="Notebook recipe"
      >
        <div className={reportClass("panel-heading")}>
          <SlidersHorizontal size={15} />
          <h2>Notebook recipe</h2>
        </div>
        <p className={reportClass("panel-description")}>
          Decide what Sofia should pay attention to. Every notebook is saved to
          your Notebooks list.
        </p>
        <fieldset disabled={disabled} className={reportClass("config-fields")}>
          <Choice
            label="Data source"
            value={scope.source}
            onChange={(value) =>
              updateScope({ source: value as Scope["source"] })
            }
            options={[
              { value: "both", label: "Simulations + monitoring" },
              { value: "simulations", label: "Simulations only" },
              { value: "monitoring", label: "Monitoring only" },
            ]}
            disabled={disabled}
          />
          {!monitoringOnly && (
            <>
              <FormField
                label="Agents"
                description={`Select up to ${capacity.maxSimulationAgents} simulation agents with the current upload choices.`}
              >
                {(ids) => (
                  <MultiSelect
                    aria-labelledby={ids["aria-labelledby"]}
                    value={scope.agent_ids}
                    placeholder={
                      manual
                        ? "Choose agents"
                        : scope.top_n
                          ? `Saved top ${scope.top_n} selection`
                          : "All agents"
                    }
                    searchable
                    searchPlaceholder="Search agents"
                    disabled={disabled}
                    options={bootstrap.agents.map((agent) => ({
                      value: agent.id,
                      label: agent.name,
                      disabled,
                    }))}
                    onValueChange={(agent_ids) =>
                      updateScope({ agent_ids, top_n: null }, true)
                    }
                    footer={
                      <label className={reportClass("check-row")}>
                        <Checkbox
                          aria-label="All agents"
                          checked={!manual && !scope.top_n}
                          disabled={disabled}
                          onCheckedChange={() =>
                            updateScope({ agent_ids: [], top_n: null }, false)
                          }
                        />
                        <span>All agents</span>
                      </label>
                    }
                  />
                )}
              </FormField>
              {bootstrap.agent_ordering?.complete === false && (
                <p className={reportClass("field-hint")}>
                  Simulation activity is incomplete; agent order may be
                  approximate.
                </p>
              )}
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="group w-full justify-start"
                  >
                    <ChevronDown
                      aria-hidden="true"
                      className="transition-transform group-data-[state=open]:rotate-180"
                    />
                    Test sets
                    <span className="ml-auto text-coval-text-muted">
                      {scope.test_set_ids.length
                        ? `${scope.test_set_ids.length} selected`
                        : "All"}
                    </span>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3 pt-3">
                  <p className={reportClass("field-hint")}>
                    Selected test sets intersect with the agents above.
                  </p>
                  <div className={reportClass("selection-list")}>
                    {bootstrap.test_sets.length ? (
                      bootstrap.test_sets.map((set) => (
                        <label
                          key={set.id}
                          className={reportClass("check-row")}
                        >
                          <Checkbox
                            aria-label={set.name}
                            checked={scope.test_set_ids.includes(set.id)}
                            disabled={disabled}
                            onCheckedChange={() =>
                              updateScope({
                                test_set_ids: toggle(
                                  scope.test_set_ids,
                                  set.id,
                                ),
                              })
                            }
                          />
                          <span>{set.name}</span>
                        </label>
                      ))
                    ) : (
                      <p className={reportClass("field-hint")}>
                        No test sets available in this data source.
                      </p>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
          {scope.source !== "simulations" && (
            <UploadedAgentSelection
              scope={scope}
              bootstrap={bootstrap}
              disabled={disabled}
              onChange={updateScope}
            />
          )}
          <ReportPeriodPicker
            config={config}
            onChange={onChange}
            defaultEnd={bootstrap.default_period_end}
            disabled={disabled}
          />
          {onRepeatChange && (
            <ReportRepeatPicker
              value={repeat}
              onChange={onRepeatChange}
              automatic={bootstrap.scheduling_available !== false}
              disabled={disabled}
              cadences={repeatCadences}
            />
          )}
          <FormField
            label="What matters to you?"
            description="Optional. Your priorities shape the analysis and follow-up recommendations."
          >
            {(ids) => (
              <Textarea
                {...ids}
                rows={3}
                maxLength={4000}
                placeholder="Focus on observed changes, customer outcomes, and what to check next…"
                value={config.instructions}
                onChange={(event) =>
                  onChange({ ...config, instructions: event.target.value })
                }
              />
            )}
          </FormField>
        </fieldset>
        {!monitoringOnly && manual && !scope.agent_ids.length && (
          <p className={reportClass("inline-error")}>
            Choose at least one agent.
          </p>
        )}
        {capacity.error && (
          <p role="alert" className={reportClass("inline-error")}>
            {capacity.error}
          </p>
        )}
        {periodError && (
          <p role="alert" className={reportClass("inline-error")}>
            {periodError}
          </p>
        )}
        <Button
          className={reportClass("generate-button")}
          loading={disabled}
          disabled={disabled || invalid}
          onClick={onGenerate}
        >
          <FileText size={15} />
          {repeat ? "Generate and schedule" : "Generate notebook"}
        </Button>
        {onScheduleNotebook && (
          <Button
            variant="secondary"
            className="mt-2 w-full"
            disabled={disabled}
            onClick={onScheduleNotebook}
          >
            <CalendarClock size={15} />
            Schedule this notebook
          </Button>
        )}
      </section>
      {savedRecipes}
    </>
  )
}
